import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Building2, TrendingUp, AlertTriangle, LifeBuoy, Layers, ShieldCheck,
  CalendarRange, Trophy, Percent, Users, Banknote, ChevronRight, Download,
} from "lucide-react";
import BudgetTrackerTab from "@/components/BudgetTrackerTab";
import SalesTargetsTab from "@/components/SalesTargetsTab";
import TechnicalTab from "@/components/TechnicalTab";
import DealEditDialog from "@/components/DealEditDialog";
import SendForecastEmailDialog from "@/components/SendForecastEmailDialog";
import LostReasonsCard from "@/components/LostReasonsCard";
import CustomerMap from "@/components/CustomerMap";
import { MapPin } from "lucide-react";

const Stat = ({ icon: Icon, label, value, sub, accent = "blue", testid }) => (
  <Card data-testid={testid} className="border-slate-200 hover:shadow-md transition-all">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500">{label}</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-2">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`h-10 w-10 rounded-md flex items-center justify-center bg-${accent}-50 text-${accent}-600 border border-${accent}-100`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Compact MRR tile for the Closed Won → "Total new MRR" breakdown strip.
// Two legs (new business + renewals) sit side-by-side with the combined
// total on the right. The total tile is emphasised so an exec sees the
// headline answer in half a second without having to add the two numbers
// in their head.
const NewMrrCard = ({ label, amount, dealsCount, annualised, emphasised, hint, accent = "emerald", testid }) => (
  <Card
    data-testid={testid}
    className={`${emphasised ? `border-${accent}-300 bg-${accent}-50/40` : "border-slate-200 bg-white"} hover:shadow-md transition-all`}
  >
    <CardContent className="p-4">
      <div className={`text-[11px] font-mono uppercase tracking-[0.14em] ${emphasised ? `text-${accent}-700` : "text-slate-500"}`}>
        {label}
      </div>
      <div className={`font-heading ${emphasised ? "text-3xl" : "text-2xl"} font-bold text-slate-900 mt-1`}>
        {fmtMoney(amount)}
      </div>
      <div className="text-[11px] text-slate-500 mt-1">
        {dealsCount} {dealsCount === 1 ? "deal" : "deals"}
        {annualised ? ` · ${fmtMoney(amount * 12)} annualised` : ""}
      </div>
      {hint && <div className="text-[10px] text-slate-400 italic mt-2 leading-snug">{hint}</div>}
    </CardContent>
  </Card>
);



export default function Dashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Portfolio Overview</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Xentra Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">Account health, pipeline, forecast and KPIs across all customers.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent border-b border-slate-200 rounded-none w-full justify-start h-auto p-0 gap-1">
          {[
            ["overview", "Overview"],
            ["forecast", "Forecast"],
            ["kpis", "Key KPIs"],
            ["budget", "Budget Tracker"],
            ["targets", "Sales Targets"],
            ["technical", "Technical"],
          ].map(([v, l]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`dashboard-tab-${v}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-600 data-[state=active]:text-blue-700 data-[state=active]:shadow-none px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-900"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-6"><OverviewTab /></TabsContent>
        <TabsContent value="forecast" className="mt-6"><ForecastTab /></TabsContent>
        <TabsContent value="kpis" className="mt-6"><KpisTab /></TabsContent>
        <TabsContent value="budget" className="mt-6"><BudgetTrackerTab /></TabsContent>
        <TabsContent value="targets" className="mt-6"><SalesTargetsTab /></TabsContent>
        <TabsContent value="technical" className="mt-6"><TechnicalTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------- Overview (existing dashboard) ---------------- */
function OverviewTab() {
  const [data, setData] = useState(null);
  const [cm, setCm] = useState(null);
  const [mapData, setMapData] = useState(null);
  useEffect(() => {
    (async () => { const r = await api.get("/dashboard"); setData(r.data); })();
    (async () => {
      try {
        const r = await api.get("/chartmogul/portfolio");
        setCm(r.data);
      } catch (e) {
        // ChartMogul is optional — surface in console for ops but don't break the dashboard.
        console.warn("chartmogul portfolio load failed:", e?.message);
      }
    })();
    (async () => {
      try {
        const r = await api.get("/dashboard/map");
        setMapData(r.data);
      } catch (e) {
        console.warn("map load failed:", e?.message);
      }
    })();
  }, []);
  if (!data) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;

  const stagePalette = ["bg-blue-600", "bg-blue-500", "bg-blue-400", "bg-blue-300", "bg-slate-300"];
  const maxStage = Math.max(...data.stage_breakdown.map((s) => s.value), 1);

  const cmArr = cm?.global?.arr;
  const cmMrr = cm?.global?.mrr;
  const arrValue = cmArr != null ? cmArr : data.total_arr;
  const mrrValue = cmMrr != null ? cmMrr : data.total_mrr;
  const arrLabel = cmArr != null ? "Run-rate ARR" : "Total ARR";
  const arrSub = cmArr != null
    ? `Live · MRR ${fmtMoney(mrrValue)} · ${cm?.global?.customers ?? "—"} customers`
    : `MRR ${fmtMoney(mrrValue)}`;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        <Stat icon={Building2} label="Live customers" value={data.live_customers || 0} sub={`${(data.accounts_total || 0) - (data.live_customers || 0)} non-active · ${data.accounts_total || 0} total`} testid="kpi-accounts" />
        <Stat icon={TrendingUp} label={arrLabel} value={fmtMoney(arrValue)} sub={arrSub} testid="kpi-arr" accent="emerald" />
        <Stat icon={ShieldCheck} label="Open pipeline" value={fmtMoney(data.open_pipeline_value)} sub={`${data.open_deals} deals · weighted ${fmtMoney(data.weighted_forecast)}`} testid="kpi-pipeline" />
        <Stat icon={LifeBuoy} label="Open tickets" value={data.open_tickets} sub={`${data.high_priority_tickets} high/urgent`} accent="amber" testid="kpi-tickets" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
            <CardTitle className="text-base font-heading">Pipeline by stage</CardTitle>
            <Badge variant="outline" className="font-mono text-[10px]">OPEN DEALS</Badge>
          </CardHeader>
          <CardContent className="p-5">
            {data.stage_breakdown.length === 0 ? (
              <div className="text-sm text-slate-500 py-8 text-center">No active deals yet.</div>
            ) : (
              <div className="space-y-3">
                {data.stage_breakdown.map((s, i) => (
                  <div key={s.stage} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-slate-700 font-medium">{s.stage}</div>
                    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${stagePalette[i % stagePalette.length]} transition-all`} style={{ width: `${(s.value / maxStage) * 100}%` }} />
                    </div>
                    <div className="w-28 text-right font-mono text-sm text-slate-900">{fmtMoney(s.value)}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-base font-heading">Account health</CardTitle>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">Live customers only</div>
          </CardHeader>
          <CardContent className="p-5 space-y-4">
            {[
              { k: "green", label: "Healthy", color: "bg-emerald-500" },
              { k: "amber", label: "Watch", color: "bg-amber-500" },
              { k: "red", label: "At risk", color: "bg-red-500" },
            ].map(({ k, label, color }) => {
              const live = data.live_customers || 0;
              const health = data.health_live || data.health || {};
              const total = live || 1;
              const v = health[k] || 0;
              return (
                <div key={k}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <div className="flex items-center gap-2"><span className={`h-2 w-2 rounded-full ${color}`} /><span className="text-slate-700">{label}</span></div>
                    <div className="font-mono text-slate-500">{v}/{live}</div>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${color}`} style={{ width: `${(v / total) * 100}%` }} /></div>
                </div>
              );
            })}
            {data.at_risk_accounts > 0 && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-100">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900"><span className="font-semibold">{data.at_risk_accounts} accounts</span> flagged at risk.</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2"><Layers className="h-4 w-4 text-blue-600" />Stax adoption</CardTitle>
            <div className="text-xs text-slate-500 mt-1">Adoption across active customers only ({data.live_customers || 0} accounts).</div>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.product_adoption.length === 0 ? (
              <div className="text-sm text-slate-500 py-6">No products in catalog.</div>
            ) : data.product_adoption.map((p) => {
              const denom = data.live_customers || 0;
              const pct = denom ? Math.round((p.count / denom) * 100) : 0;
              return (
                <div key={p.name} className="p-4 rounded-md border border-slate-200 bg-slate-50/50">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                      <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mt-0.5">{p.category}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-xl font-bold text-blue-700">{p.count}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{pct}%</div>
                    </div>
                  </div>
                  <div className="mt-3 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="csm-ownership-card">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2"><Users className="h-4 w-4 text-indigo-600" />CSM Ownership</CardTitle>
            <div className="text-xs text-slate-500 mt-1">Active accounts per Customer Success Manager.</div>
          </div>
          <Badge variant="outline" className="font-mono text-[10px]">{data.live_customers || 0} ACTIVE</Badge>
        </CardHeader>
        <CardContent className="p-5">
          {(!data.by_csm || data.by_csm.length === 0) ? (
            <div className="text-sm text-slate-500 py-6">No active accounts yet.</div>
          ) : (() => {
            const rows = data.by_csm;
            const max = Math.max(1, ...rows.map((r) => r.active_accounts));
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {rows.map((r) => {
                  const unassigned = r.name === "— Unassigned —";
                  const initials = unassigned
                    ? "??"
                    : r.name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "?";
                  const pct = Math.round((r.active_accounts / max) * 100);
                  return (
                    <div
                      key={r.name}
                      className={`p-3 rounded-md border ${unassigned ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-slate-50/50"}`}
                      data-testid={`csm-row-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${unassigned ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-indigo-100 text-indigo-800 border border-indigo-200"}`}>
                          {initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm font-medium truncate ${unassigned ? "text-amber-900" : "text-slate-900"}`}>{r.name}</div>
                          <div className="mt-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full ${unassigned ? "bg-amber-500" : "bg-indigo-600"}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-heading text-xl font-bold text-indigo-700 leading-none">{r.active_accounts}</div>
                          <div className="text-[10px] text-slate-500 font-mono mt-1">ACCOUNTS</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="customer-map-card">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2"><MapPin className="h-4 w-4 text-rose-600" />Customer map</CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              Active customers plotted from their postcode.
              {mapData && (
                <span className="ml-1 font-mono">
                  {mapData.pins?.length || 0} on map
                  {mapData.accounts_without_coords ? ` · ${mapData.accounts_without_coords} without a postcode` : ""}
                </span>
              )}
            </div>
          </div>
          <Badge variant="outline" className="font-mono text-[10px]">POSTCODES.IO</Badge>
        </CardHeader>
        <CardContent className="p-5">
          {!mapData ? (
            <div className="text-sm text-slate-500 py-6">Loading map…</div>
          ) : (mapData.pins || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6" data-testid="customer-map-empty">
              No active accounts have a postcode yet. Add postcodes on individual accounts, or run <span className="font-mono">Bulk geocode</span> from the Admin area to populate the map.
            </div>
          ) : (
            <CustomerMap pins={mapData.pins} height={460} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------- Forecast Tab ---------------- */
const _isRenewal = (d) => (d.subscription_type || "").toLowerCase() === "renewal" || !!d.is_auto_renewal;

// Minimal CSV writer scoped to the Dashboard. Quotes any cell containing
// commas, quotes or newlines per RFC-4180 so Excel/Numbers/Sheets open
// the file cleanly.
function _downloadForecastCsv(filename, headers, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [headers.map(esc).join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ForecastTab() {
  const [openDealsAll, setOpenDealsAll] = useState([]); // both new + renewal open deals
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [period, setPeriod] = useState("current_quarter");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [dealType, setDealType] = useState("all"); // all | new | renewals (Closed Won toggle)
  const [includeRenewals, setIncludeRenewals] = useState(false); // open pipeline toggle
  const [closedWon, setClosedWon] = useState(null);
  const [editingDeal, setEditingDeal] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/deals");
        // Store ALL open deals; renewal inclusion is decided at render time
        // by the `includeRenewals` toggle. This preserves the user's ability
        // to see renewals when their open pipeline is renewal-heavy.
        setOpenDealsAll((r.data || []).filter((d) =>
          d.stage !== "Closed Won" && d.stage !== "Closed Lost"
        ));
        setLoadError(null);
      } catch (e) {
        setOpenDealsAll([]);
        setLoadError(e?.response?.data?.detail || e?.message || "Failed to load deals");
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  // Derived: apply the include-renewals toggle to the open dataset
  const allDeals = useMemo(() => {
    return includeRenewals ? openDealsAll : openDealsAll.filter((d) => !_isRenewal(d));
  }, [openDealsAll, includeRenewals]);

  const renewalOpenCount = useMemo(
    () => openDealsAll.filter((d) => _isRenewal(d)).length,
    [openDealsAll]
  );

  // Fetch Closed Won totals for the active period + owner + deal_type filter
  useEffect(() => {
    (async () => {
      try {
        const params = { period, deal_type: dealType };
        if (ownerFilter !== "all") params.owner_name = ownerFilter;
        const r = await api.get("/dashboard/closed-won", { params });
        setClosedWon(r.data);
      } catch (_) { setClosedWon(null); }
    })();
  }, [period, ownerFilter, dealType]);

  // Compute period range
  const periodRange = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-indexed
    const startOfMonth = (y, m) => new Date(y, m, 1);
    const endOfMonth = (y, m) => new Date(y, m + 1, 0, 23, 59, 59);
    // FY starts April (month index 3). FY quarters:
    //   Q1 Apr–Jun, Q2 Jul–Sep, Q3 Oct–Dec, Q4 Jan–Mar.
    const fqIndex = ((month - 3 + 12) % 12) >> 0; // months since April
    const fqNum = Math.floor(fqIndex / 3);        // 0..3
    const fyStartYear = month >= 3 ? year : year - 1;
    const fqRange = (offset) => {
      const target = fqNum + offset;
      const fqTarget = ((target % 4) + 4) % 4;
      const fyTargetStartYear = fyStartYear + Math.floor(target / 4);
      let startMonth = 3 + fqTarget * 3;  // 0-indexed April=3
      let startYear = fyTargetStartYear;
      if (startMonth > 11) { startMonth -= 12; startYear += 1; }
      let endMonth = startMonth + 2;
      let endYear = startYear;
      if (endMonth > 11) { endMonth -= 12; endYear += 1; }
      const fyEndYear = fyTargetStartYear + 1;
      const label = `FY${String(fyEndYear).slice(-2)} Q${fqTarget + 1} (${startOfMonth(startYear, startMonth).toLocaleString("en-GB", { month: "short" })}–${startOfMonth(endYear, endMonth).toLocaleString("en-GB", { month: "short", year: "numeric" })})`;
      return { start: startOfMonth(startYear, startMonth), end: endOfMonth(endYear, endMonth), label };
    };
    if (period === "current_month") {
      return { start: startOfMonth(year, month), end: endOfMonth(year, month), label: now.toLocaleString("en-GB", { month: "long", year: "numeric" }) };
    }
    if (period === "next_month") {
      const ny = month === 11 ? year + 1 : year;
      const nm = (month + 1) % 12;
      return { start: startOfMonth(ny, nm), end: endOfMonth(ny, nm), label: new Date(ny, nm, 1).toLocaleString("en-GB", { month: "long", year: "numeric" }) };
    }
    if (period === "last_month") {
      const ly = month === 0 ? year - 1 : year;
      const lm = (month + 11) % 12;
      return { start: startOfMonth(ly, lm), end: endOfMonth(ly, lm), label: new Date(ly, lm, 1).toLocaleString("en-GB", { month: "long", year: "numeric" }) };
    }
    if (period === "current_quarter") return fqRange(0);
    if (period === "next_quarter")    return fqRange(1);
    if (period === "last_quarter")    return fqRange(-1);
    return { start: null, end: null, label: "All time" };
  }, [period]);

  const owners = useMemo(() => {
    const set = new Set(allDeals.map((d) => d.owner_name).filter(Boolean));
    return Array.from(set).sort();
  }, [allDeals]);

  const filtered = useMemo(() => {
    return allDeals.filter((d) => {
      if (ownerFilter !== "all" && d.owner_name !== ownerFilter) return false;
      if (!periodRange.start) return true;
      if (!d.close_date) return false;
      const cd = new Date(d.close_date);
      return cd >= periodRange.start && cd <= periodRange.end;
    });
  }, [allDeals, ownerFilter, periodRange]);

  const totals = useMemo(() => {
    const revenue = filtered.reduce((s, d) => s + (d.revenue || 0), 0);
    const weighted = filtered.reduce((s, d) => s + (d.forecast_value || 0), 0);
    const mrr = filtered.reduce((s, d) => s + (d.mrr || 0), 0);
    // Weighted MRR = sum of (mrr × probability) — same logic as revenue forecast but on MRR
    const weightedMrr = filtered.reduce((s, d) => s + ((d.mrr || 0) * ((d.probability || 0) / 100)), 0);
    return { revenue, weighted, mrr, weightedMrr, count: filtered.length };
  }, [filtered]);

  const byStage = useMemo(() => {
    const m = {};
    filtered.forEach((d) => {
      const key = d.stage || "(no stage)";
      if (!m[key]) m[key] = { stage: key, count: 0, revenue: 0, weighted: 0 };
      m[key].count += 1;
      m[key].revenue += d.revenue || 0;
      m[key].weighted += d.forecast_value || 0;
    });
    return Object.values(m).sort((a, b) => b.revenue - a.revenue);
  }, [filtered]);
  const maxStage = Math.max(...byStage.map((s) => s.revenue), 1);

  const topDeals = useMemo(() => {
    return filtered.slice().sort((a, b) => (b.revenue || 0) - (a.revenue || 0)).slice(0, 10);
  }, [filtered]);

  // Pipeline split by STAX product. A single deal that bundles multiple STAX
  // products contributes its revenue + weighted forecast to each product it
  // includes (so the totals here are >= the headline pipeline figure — this
  // is intentional and labelled on the card so sales ops don't double-count).
  const byStax = useMemo(() => {
    const bucket = {};
    filtered.forEach((d) => {
      const names =
        (Array.isArray(d.stax_products) && d.stax_products.length ? d.stax_products : null) ||
        (Array.isArray(d.product_items) ? d.product_items.map((p) => p.name) : []);
      if (!names.length) return;
      names.forEach((name) => {
        if (!bucket[name]) bucket[name] = { name, count: 0, revenue: 0, weighted: 0, mrr: 0 };
        bucket[name].count += 1;
        bucket[name].revenue += d.revenue || 0;
        bucket[name].weighted += d.forecast_value || 0;
        bucket[name].mrr += d.mrr || 0;
      });
    });
    return Object.values(bucket).sort((a, b) => b.revenue - a.revenue);
  }, [filtered]);
  const maxStax = Math.max(...byStax.map((s) => s.revenue), 1);

  if (!loaded) return <div className="text-slate-500 font-mono text-sm" data-testid="forecast-loading">Loading forecast…</div>;
  if (loadError) return (
    <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="forecast-error">
      Could not load forecast — {loadError}. <button className="underline ml-1" onClick={() => window.location.reload()}>Retry</button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="forecast-dashboard">
      {/* Period chips + owner filter */}
      <div className="flex flex-wrap items-center gap-3" data-testid="forecast-toolbar">
        <div className="flex flex-wrap gap-2">
          {[
            { v: "last_month", label: "Last month" },
            { v: "current_month", label: "Current month" },
            { v: "next_month", label: "Next month" },
            { v: "last_quarter", label: "Last quarter (FY)" },
            { v: "current_quarter", label: "Current quarter" },
            { v: "next_quarter", label: "Next quarter" },
            { v: "all", label: "All time" },
          ].map((p) => (
            <button
              key={p.v}
              data-testid={`forecast-period-${p.v}`}
              onClick={() => setPeriod(p.v)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition shadow-sm ${
                period === p.v
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer select-none" title="Include renewal deals in the open pipeline (Top 10 / Pipeline by stage). Off by default.">
            <input
              type="checkbox"
              data-testid="forecast-include-renewals-toggle"
              checked={includeRenewals}
              onChange={(e) => setIncludeRenewals(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-xs font-medium text-slate-700">
              Include renewals
              {renewalOpenCount > 0 && (
                <span className="ml-1 text-[10px] font-mono text-slate-500">({renewalOpenCount})</span>
              )}
            </span>
          </label>
          <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Owner</span>
          <select
            data-testid="forecast-owner-filter"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 cursor-pointer hover:bg-slate-50"
          >
            <option value="all">All owners</option>
            {owners.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {/* Email forecast — opens a dialog where the sales lead picks
              recipients, adds notes and the "Expected MRR" judgement call,
              then dispatches via Microsoft 365. Period defaults to whatever
              the Forecast tab is currently showing so the two stay in sync. */}
          <SendForecastEmailDialog
            defaultPeriod={period === "all" ? "current_month" : period}
          />
        </div>
      </div>

      <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700">
        Filter: {periodRange.label} · {ownerFilter === "all" ? "All owners" : ownerFilter}{includeRenewals ? " · Incl. renewals" : ""}
      </div>

      {/* KPI row — all MRR-based */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={ShieldCheck} label="Open pipeline MRR" value={fmtMoney(totals.mrr)} sub={`${totals.count} deals`} testid="forecast-pipeline" />
        <Stat icon={TrendingUp} label="Weighted MRR forecast" value={fmtMoney(totals.weightedMrr)} sub="MRR × probability" accent="emerald" testid="forecast-weighted" />
        <Stat
          icon={Trophy}
          label={dealType === "renewals" ? "Renewal uplift MRR" : dealType === "new" ? "Closed Won MRR · New" : "Closed Won · Total new MRR"}
          value={fmtMoney(closedWon?.mrr_total || 0)}
          sub={
            closedWon
              ? `${closedWon.count} deals · ${closedWon.label}` +
                (closedWon.breakdown && dealType === "all"
                  ? ` · New ${fmtMoney(closedWon.breakdown.new.mrr_total)} + Uplift ${fmtMoney(closedWon.breakdown.renewals.mrr_total)}`
                  : "")
              : "—"
          }
          accent="emerald"
          testid="forecast-closed-won"
        />
        <Stat icon={Banknote} label="Annualised MRR (ARR)" value={fmtMoney(totals.mrr * 12)} sub="Open MRR × 12" accent="blue" testid="forecast-mrr" />
        <Stat icon={CalendarRange} label="Avg MRR / deal" value={fmtMoney(totals.count ? totals.mrr / totals.count : 0)} sub="Per open deal" accent="violet" testid="forecast-avg" />
      </div>

      {/* Closed Won — total new MRR breakdown.
          Executives routinely ask "how much NEW MRR did we book this period?".
          Policy: new MRR = new business MRR + the manual UPLIFT typed on each
          renewal. A flat renewal (no uplift) credits £0 — only the
          incremental uplift the owner recorded counts as new MRR. This strip
          surfaces both legs side-by-side alongside the combined total so the
          answer is instantly readable without opening the deals table. The
          Excel download for the full table lives on the Closed Won deals
          card below (next to the All/New/Renewals toggle). */}
      {closedWon && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="forecast-new-mrr-breakdown">
          <NewMrrCard
            label="New business MRR"
            amount={closedWon.breakdown?.new?.mrr_total || 0}
            dealsCount={closedWon.breakdown?.new?.count || 0}
            accent="emerald"
            testid="new-mrr-card-new"
          />
          <NewMrrCard
            label="Renewal uplift MRR"
            amount={closedWon.breakdown?.renewals?.mrr_total || 0}
            dealsCount={closedWon.breakdown?.renewals?.count || 0}
            hint={`Sum of the "Uplift MRR" the owner recorded on each renewal. Flat renewals credit £0 — only the incremental uplift counts as new MRR${
              closedWon.breakdown?.renewals?.mrr_full
                ? ` (full renewal MRR booked: ${fmtMoney(closedWon.breakdown.renewals.mrr_full)}).`
                : "."
            }`}
            accent="blue"
            testid="new-mrr-card-renewals"
          />
          <NewMrrCard
            label="Total new MRR"
            amount={(closedWon.breakdown?.new?.mrr_total || 0) + (closedWon.breakdown?.renewals?.mrr_total || 0)}
            dealsCount={(closedWon.breakdown?.new?.count || 0) + (closedWon.breakdown?.renewals?.count || 0)}
            annualised
            emphasised
            accent="emerald"
            testid="new-mrr-card-total"
          />
        </div>
      )}

      {/* Top 10 deals */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-600" />Top 10 deals
            </CardTitle>
            <button
              type="button"
              onClick={() => {
                // Export the FULL filtered dataset (not just the top 10 shown)
                // because that's the "on-screen forecast" the user filtered to
                // via period, owner and the renewals toggle. A top-10-only
                // export would be surprising for finance workflows.
                const headers = [
                  "Account", "Deal", "Owner", "Stage", "Subscription type",
                  "Revenue", "MRR", "Weighted forecast", "Probability %", "Close date",
                ];
                const sorted = filtered.slice().sort(
                  (a, b) => (b.revenue || 0) - (a.revenue || 0)
                );
                const rows = sorted.map((d) => [
                  d.account_name || "",
                  d.name || "",
                  d.owner_name || "",
                  d.stage || "",
                  d.subscription_type || (d.is_auto_renewal ? "Renewal" : ""),
                  d.revenue ?? 0,
                  d.mrr ?? 0,
                  d.forecast_value ?? 0,
                  d.probability ?? 0,
                  d.close_date ? new Date(d.close_date).toISOString().slice(0, 10) : "",
                ]);
                const periodSlug = (periodRange.label || "forecast")
                  .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                _downloadForecastCsv(`xentra-forecast-${periodSlug}.csv`, headers, rows);
              }}
              disabled={filtered.length === 0}
              title={filtered.length === 0 ? "No deals to export" : `Export ${filtered.length} deals to CSV`}
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-600 hover:text-blue-700 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed px-2.5 py-1.5 rounded-md border border-slate-200 transition-colors"
              data-testid="forecast-top-deals-csv-btn"
            >
              <Download className="h-3.5 w-3.5" />
              CSV
            </button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {topDeals.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="forecast-top-deals-empty">
              <div className="mb-3">
                No open deals close in this period
                {allDeals.length > 0 ? ` (${allDeals.length} open ${includeRenewals ? "" : "new "}deals overall — try widening the window)` : ""}
                {allDeals.length === 0 && !includeRenewals && renewalOpenCount > 0
                  ? ` — your open pipeline is ${renewalOpenCount} renewal deal${renewalOpenCount === 1 ? "" : "s"}`
                  : ""}.
              </div>
              <div className="inline-flex gap-2 flex-wrap justify-center">
                {allDeals.length > 0 && period !== "current_quarter" && (
                  <button
                    className="px-3 py-1 text-xs rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => setPeriod("current_quarter")}
                    data-testid="forecast-empty-switch-quarter"
                  >Try current quarter</button>
                )}
                {allDeals.length > 0 && period !== "all" && (
                  <button
                    className="px-3 py-1 text-xs rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => setPeriod("all")}
                    data-testid="forecast-empty-switch-all"
                  >Show all time</button>
                )}
                {!includeRenewals && renewalOpenCount > 0 && (
                  <button
                    className="px-3 py-1 text-xs rounded-full border border-blue-200 text-blue-700 hover:bg-blue-50 font-medium"
                    onClick={() => setIncludeRenewals(true)}
                    data-testid="forecast-empty-include-renewals"
                  >Include {renewalOpenCount} renewal{renewalOpenCount === 1 ? "" : "s"}</button>
                )}
                {ownerFilter !== "all" && (
                  <button
                    className="px-3 py-1 text-xs rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50"
                    onClick={() => setOwnerFilter("all")}
                    data-testid="forecast-empty-clear-owner"
                  >Clear owner filter</button>
                )}
              </div>
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="forecast-top-deals">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Account · Deal</th>
                  <th className="px-4 py-2">Owner</th>
                  <th className="px-4 py-2">Stage</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2 text-right">MRR</th>
                  <th className="px-4 py-2 text-right">Weighted</th>
                  <th className="px-4 py-2 text-right">Prob</th>
                  <th className="px-4 py-2">Close</th>
                </tr>
              </thead>
              <tbody>
                {topDeals.map((d, i) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer" onClick={() => nav(`/app/accounts/${d.account_id}`)}>
                    <td className="px-4 py-2 font-mono text-slate-400">{i + 1}</td>
                    <td className="px-4 py-2">
                      <div className="text-xs text-slate-500">{d.account_name}</div>
                      <div className="font-medium text-slate-900">{d.name}</div>
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600">{d.owner_name || "—"}</td>
                    <td className="px-4 py-2 text-xs text-slate-600">{d.stage}</td>
                    <td className="px-4 py-2 text-right font-mono font-medium">{fmtMoney(d.revenue)}</td>
                    <td className="px-4 py-2 text-right font-mono text-blue-700">{fmtMoney(d.mrr)}</td>
                    <td className="px-4 py-2 text-right font-mono text-emerald-700">{fmtMoney(d.forecast_value)}</td>
                    <td className="px-4 py-2 text-right text-xs font-mono">{d.probability}%</td>
                    <td className="px-4 py-2 text-xs text-slate-500">{fmtDate(d.close_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pipeline by stage */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading">Pipeline by stage</CardTitle>
        </CardHeader>
        <CardContent className="p-5">
          {byStage.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-4">No deals in this period.</div>
          ) : (
            <div className="space-y-2">
              {byStage.map((s) => {
                const pct = (s.revenue / maxStage) * 100;
                const wpct = (s.weighted / maxStage) * 100;
                return (
                  <div key={s.stage} className="flex items-center gap-3" data-testid={`forecast-stage-${s.stage.replace(/\s+/g, '-')}`}>
                    <div className="w-44 text-sm text-slate-700">{s.stage}</div>
                    <div className="flex-1 relative h-6 bg-slate-100 rounded">
                      <div className="absolute inset-y-0 left-0 bg-blue-200 rounded" style={{ width: `${pct}%` }} title={`Revenue ${fmtMoney(s.revenue)}`} />
                      <div className="absolute inset-y-0 left-0 bg-emerald-500 rounded" style={{ width: `${wpct}%` }} title={`Weighted ${fmtMoney(s.weighted)}`} />
                      <div className="absolute inset-y-0 right-2 flex items-center text-[11px] font-mono text-slate-700">
                        {fmtMoney(s.revenue)} · <span className="text-emerald-700 ml-1">{fmtMoney(s.weighted)}</span>
                      </div>
                    </div>
                    <div className="w-12 text-right text-xs font-mono text-slate-500">{s.count}</div>
                  </div>
                );
              })}
              <div className="mt-4 flex gap-4 text-xs">
                <span className="flex items-center gap-2"><span className="h-2 w-3 rounded bg-blue-200" />Revenue</span>
                <span className="flex items-center gap-2"><span className="h-2 w-3 rounded bg-emerald-500" />Weighted</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ForecastByOwner />

      {/* Closed Won deals */}
      <Card className="border-emerald-200" data-testid="forecast-closed-won-table">
        <CardHeader className="border-b border-emerald-100 py-3 bg-emerald-50/40">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Trophy className="h-4 w-4 text-emerald-700" />
              Closed Won deals
              <span className="text-xs font-mono text-emerald-700 font-normal ml-2">
                {closedWon?.count || 0} deals · {closedWon?.label || ""} · {fmtMoney(closedWon?.mrr_total || 0)} MRR
              </span>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Segmented toggle: All / New / Renewals */}
              <div className="inline-flex bg-white border border-emerald-200 rounded-full p-0.5 shadow-sm" data-testid="closed-won-type-toggle">
                {[
                  { v: "all", label: "All", count: (closedWon?.breakdown?.new?.count || 0) + (closedWon?.breakdown?.renewals?.count || 0) },
                  { v: "new", label: "New business", count: closedWon?.breakdown?.new?.count || 0 },
                  { v: "renewals", label: "Renewals", count: closedWon?.breakdown?.renewals?.count || 0 },
                ].map((o) => {
                  const active = dealType === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => setDealType(o.v)}
                      data-testid={`closed-won-type-${o.v}`}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
                        active
                          ? "bg-emerald-600 text-white shadow-sm"
                          : "text-emerald-800 hover:bg-emerald-50"
                      }`}
                    >
                      {o.label}
                      <span className={`inline-block min-w-[1.5em] text-[10px] font-mono px-1 rounded ${
                        active ? "bg-emerald-800/40 text-white" : "bg-emerald-100 text-emerald-700"
                      }`}>{o.count}</span>
                    </button>
                  );
                })}
              </div>
              {/* Excel download — mirrors the current period / owner / type filter.
                  Lives on the table card itself so it's immediately next to the
                  rows it exports. */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    const params = { period, deal_type: dealType };
                    if (ownerFilter !== "all") params.owner_name = ownerFilter;
                    const r = await api.get("/dashboard/closed-won/export", {
                      params, responseType: "blob",
                    });
                    const disp = r.headers?.["content-disposition"] || "";
                    const match = /filename="([^"]+)"/.exec(disp);
                    const periodSlug = (closedWon?.label || "closed-won")
                      .toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                    const filename = (match && match[1]) || `xentra-closed-won-${periodSlug}.xlsx`;
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = filename;
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    console.error("Closed Won export failed:", e);
                    toast.error(e?.response?.data?.detail || "Export failed — please try again");
                  }
                }}
                disabled={!closedWon?.count}
                title={!closedWon?.count ? "No Closed Won deals in this filter" : `Export ${closedWon.count} Closed Won deals to Excel`}
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-emerald-800 bg-white hover:bg-emerald-50 border border-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-full shadow-sm transition-colors"
                data-testid="closed-won-table-xlsx-btn"
              >
                <Download className="h-3.5 w-3.5" />
                Excel
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!closedWon?.items?.length ? (
            <div className="text-sm text-slate-500 text-center py-8">
              {dealType === "renewals"
                ? "No renewals closed won in this period."
                : dealType === "new"
                ? "No new-business deals closed won in this period."
                : "No deals closed won in this period."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2">Account</th>
                  <th className="px-4 py-2">Deal</th>
                  <th className="px-4 py-2">Owner</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2 text-right">MRR</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2 text-right">Term</th>
                  <th className="px-4 py-2 text-right">Closed</th>
                </tr>
              </thead>
              <tbody>
                {closedWon.items.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setEditingDeal(d)}
                    className="border-b border-slate-100 hover:bg-emerald-50/40 cursor-pointer"
                    data-testid={`closed-won-row-${d.id}`}
                  >
                    <td
                      className="px-4 py-2.5 text-sm text-blue-700 hover:underline"
                      onClick={(e) => { e.stopPropagation(); nav(`/app/accounts/${d.account_id}`); }}
                    >
                      {d.account_name || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-slate-900 font-medium">{d.name}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{d.owner_name || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        d.subscription_type === "Renewal" ? "bg-blue-50 text-blue-700"
                          : d.subscription_type === "Upsell" ? "bg-violet-50 text-violet-700"
                          : "bg-emerald-50 text-emerald-700"
                      }`}>{d.subscription_type || "New Business"}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-sm text-emerald-700 font-semibold">{fmtMoney(d.mrr || 0)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-sm text-slate-700">{fmtMoney(d.revenue || 0)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-500">{d.term_months || "—"}m</td>
                    <td className="px-4 py-2.5 text-right text-xs text-slate-500">{fmtDate(d.close_date)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-emerald-50/60 border-t border-emerald-200">
                <tr>
                  <td colSpan={4} className="px-4 py-2.5 text-right text-xs font-mono uppercase tracking-wider text-emerald-800">Totals</td>
                  <td className="px-4 py-2.5 text-right font-mono text-sm font-bold text-emerald-800">{fmtMoney(closedWon.mrr_total)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-sm font-bold text-slate-800">{fmtMoney(closedWon.revenue_total)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pipeline split by STAX product — appended at the bottom of the
          Forecast tab so sales ops can see which products carry the pipeline.
          A deal bundling multiple STAX products contributes to each row;
          this is called out below the title so the totals aren't misread. */}
      <Card className="border-slate-200" data-testid="forecast-by-stax">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-blue-600" /> Pipeline split by STAX product
          </CardTitle>
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mt-1">
            {periodRange.label} · {ownerFilter === "all" ? "All owners" : ownerFilter}
            {includeRenewals ? " · Incl. renewals" : ""}
            · a deal with multiple products counts in each row
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {byStax.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center" data-testid="forecast-by-stax-empty">
              No STAX products tagged on open deals in this window yet.
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="forecast-by-stax-list">
              {byStax.map((s) => {
                const pct = (s.revenue / maxStax) * 100;
                return (
                  <div key={s.name} className="flex items-center gap-3" data-testid={`forecast-by-stax-row-${s.name}`}>
                    <div className="w-56 text-sm text-slate-700 font-medium truncate" title={s.name}>{s.name}</div>
                    <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-28 text-right font-mono text-sm text-slate-900">{fmtMoney(s.revenue)}</div>
                    <div className="w-28 text-right font-mono text-xs text-emerald-700" title="Weighted forecast · MRR × probability equivalent on revenue">
                      {fmtMoney(s.weighted)}
                    </div>
                    <div className="w-16 text-right font-mono text-[11px] text-slate-500">{s.count} deal{s.count === 1 ? "" : "s"}</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Closed Lost reasons analysis — bottom of the tab */}
      <LostReasonsCard owners={owners} />


      {editingDeal && (
        <DealEditDialog
          deal={editingDeal}
          onClose={() => setEditingDeal(null)}
          onSaved={async () => {
            // Refresh both lists after edit
            const r = await api.get("/deals");
            setOpenDealsAll((r.data || []).filter((d) =>
              d.stage !== "Closed Won" && d.stage !== "Closed Lost"
            ));
            try {
              const params = { period, deal_type: dealType };
              if (ownerFilter !== "all") params.owner_name = ownerFilter;
              const r2 = await api.get("/dashboard/closed-won", { params });
              setClosedWon(r2.data);
            } catch (_) { /* ignore — closed-won reload is best-effort */ }
            setEditingDeal(null);
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Forecast by Owner ---------------- */
function ForecastByOwner() {
  const [period, setPeriod] = useState("current_month");
  const [dealType, setDealType] = useState("new"); // new | renewals
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/dashboard/forecast/by-owner?period=${period}&deal_type=${dealType}`);
        setData(r.data);
        setExpanded({});
      } finally {
        setLoading(false);
      }
    })();
  }, [period, dealType]);

  const max = Math.max(...(data?.owners.map((o) => o.weighted) || [1]), 1);

  const periods = [
    ["current_month", "Current month"],
    ["next_month", "Next month"],
    ["current_quarter", "Current quarter"],
    ["next_quarter", "Next quarter"],
  ];

  const toggle = (owner) => setExpanded((e) => ({ ...e, [owner]: !e[owner] }));

  return (
    <Card className="border-slate-200" data-testid="forecast-by-owner">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-600" />Forecast per owner
              {/* Segmented New / Renewals toggle */}
              <div className="inline-flex bg-slate-100 rounded-full p-0.5 ml-2" data-testid="forecast-by-owner-deal-type">
                {[["new", "New business"], ["renewals", "Renewals"]].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setDealType(v)}
                    data-testid={`forecast-by-owner-type-${v}`}
                    className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition ${
                      dealType === v
                        ? "bg-white text-blue-700 shadow-sm border border-slate-200"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              {dealType === "renewals" ? "Upcoming renewals in this window" : "Pipeline excluding renewals"}
              {" · "}Fiscal year starts April · Click an owner to see their deals
              {data?.label && <span className="ml-1 text-slate-700 font-medium">· {data.label}</span>}
            </div>
          </div>
          <div className="flex gap-1 flex-wrap" data-testid="forecast-by-owner-periods">
            {periods.map(([v, l]) => (
              <button
                key={v}
                data-testid={`forecast-by-owner-period-${v}`}
                onClick={() => setPeriod(v)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  period === v
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading || !data ? (
          <div className="p-8 text-center text-sm text-slate-500 font-mono">Loading owner forecast…</div>
        ) : data.owners.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No deals close in this window.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-5 border-b border-slate-100 bg-slate-50/40">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Revenue</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-0.5">{fmtMoney(data.totals.revenue)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Weighted MRR</div>
                <div className="font-heading text-xl font-bold text-emerald-700 mt-0.5" title="Sum of MRR × probability across the pipeline in this window">{fmtMoney(data.totals.weighted)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">GP</div>
                <div className="font-heading text-xl font-bold text-blue-700 mt-0.5">{fmtMoney(data.totals.gp)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Deals</div>
                <div className="font-heading text-xl font-bold text-slate-900 mt-0.5">{data.totals.deals_count}</div>
              </div>
            </div>
            <table className="w-full text-sm" data-testid="forecast-by-owner-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2 w-8"></th>
                  <th className="px-4 py-2">Owner</th>
                  <th className="px-4 py-2 text-right">Deals</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2 text-right">GP</th>
                  <th className="px-4 py-2 text-right">MRR</th>
                  <th className="px-4 py-2 text-right" title="Sum of MRR × probability">Weighted MRR</th>
                  <th className="px-4 py-2 w-1/5">Share</th>
                </tr>
              </thead>
              <tbody>
                {data.owners.map((o) => {
                  const pct = (o.weighted / max) * 100;
                  const isOpen = !!expanded[o.owner];
                  const ownerKey = o.owner.toLowerCase().replace(/\s+/g, '-');
                  return (
                    <React.Fragment key={o.owner}>
                      <tr
                        className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer select-none"
                        onClick={() => toggle(o.owner)}
                        data-testid={`forecast-owner-${ownerKey}`}
                      >
                        <td className="px-4 py-2 text-slate-400">
                          <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90 text-blue-600" : ""}`} />
                        </td>
                        <td className="px-4 py-2 font-medium text-slate-900">{o.owner}</td>
                        <td className="px-4 py-2 text-right font-mono">{o.deals_count}</td>
                        <td className="px-4 py-2 text-right font-mono">{fmtMoney(o.revenue)}</td>
                        <td className="px-4 py-2 text-right font-mono text-blue-700">{fmtMoney(o.gp)}</td>
                        <td className="px-4 py-2 text-right font-mono text-slate-600">{fmtMoney(o.mrr)}</td>
                        <td className="px-4 py-2 text-right font-mono text-emerald-700 font-semibold">{fmtMoney(o.weighted)}</td>
                        <td className="px-4 py-2">
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/60" data-testid={`forecast-owner-deals-${ownerKey}`}>
                          <td></td>
                          <td colSpan={7} className="px-4 py-3">
                            <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
                              <table className="w-full text-[13px]">
                                <thead>
                                  <tr className="bg-slate-50 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                                    <th className="px-3 py-1.5">Account · Deal</th>
                                    <th className="px-3 py-1.5">Stage</th>
                                    <th className="px-3 py-1.5">Close</th>
                                    <th className="px-3 py-1.5 text-right">Term</th>
                                    <th className="px-3 py-1.5 text-right">Revenue</th>
                                    <th className="px-3 py-1.5 text-right">GP</th>
                                    <th className="px-3 py-1.5 text-right">MRR</th>
                                    <th className="px-3 py-1.5 text-right">Prob</th>
                                    <th className="px-3 py-1.5 text-right" title="MRR × probability">Weighted MRR</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {o.deals.map((d) => (
                                    <tr
                                      key={d.id}
                                      className="border-b border-slate-100 last:border-b-0 hover:bg-blue-50/30 cursor-pointer"
                                      onClick={(e) => { e.stopPropagation(); nav(`/app/accounts/${d.account_id}`); }}
                                      data-testid={`forecast-deal-row-${d.id}`}
                                    >
                                      <td className="px-3 py-1.5">
                                        <div className="text-[11px] text-slate-500">{d.account_name}</div>
                                        <div className="font-medium text-slate-900 text-[13px]">{d.name}</div>
                                      </td>
                                      <td className="px-3 py-1.5 text-[11px] text-slate-600">{d.stage}</td>
                                      <td className="px-3 py-1.5 text-[11px] text-slate-500">{fmtDate(d.close_date)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-[11px] text-slate-500">{d.term_months}m</td>
                                      <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(d.revenue)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-blue-700">{fmtMoney(d.gp)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-slate-600">{fmtMoney(d.mrr)}</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-[11px] text-slate-500">{d.probability}%</td>
                                      <td className="px-3 py-1.5 text-right font-mono text-emerald-700 font-semibold">{fmtMoney(d.weighted_mrr)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </CardContent>
    </Card>
  );
}
/* ---------------- KPIs Tab ---------------- */
function KpisTab() {
  const [data, setData] = useState(null);
  const [cm, setCm] = useState(null);
  const [agg, setAgg] = useState(null);
  const [accountsMap, setAccountsMap] = useState({});
  const nav = useNavigate();
  useEffect(() => {
    (async () => {
      const r = await api.get("/dashboard/kpis");
      setData(r.data);
      try {
        const c = await api.get("/chartmogul/portfolio");
        setCm(c.data);
      } catch (_) { /* ChartMogul optional */ }
      try {
        const a = await api.get("/dashboard/budget-aggregates?months_back=12");
        setAgg(a.data);
      } catch (_) { /* Xero optional */ }
      try {
        // Enrich the ChartMogul-sourced top customers with industry / owner / health
        const all = await api.get("/accounts");
        const map = {};
        for (const acc of all.data) map[acc.id] = acc;
        setAccountsMap(map);
      } catch (_) { /* enrichment optional */ }
    })();
  }, []);
  if (!data) return <div className="text-slate-500 font-mono text-sm">Loading KPIs…</div>;

  const indMax = Math.max(...data.by_industry.map((i) => i.count), 1);

  const cmMrr = cm?.global?.mrr;
  const cmArr = cm?.global?.arr;
  const cmCustomers = cm?.global?.customers;
  const mrrValue = cmMrr != null ? cmMrr : data.total_mrr;
  const arrValue = cmArr != null ? cmArr : data.total_arr;
  const customersValue = cmCustomers != null ? cmCustomers : data.num_customers;
  const mrrSub = cmMrr != null ? "Live · ChartMogul" : `${data.won_deals_count} won deals`;
  const arrSub = cmArr != null ? "Live · ChartMogul" : "MRR × 12";
  const customersSub = cmCustomers != null ? "Live · ChartMogul" : `of ${data.num_accounts} accounts`;
  const avgMrr = customersValue ? mrrValue / customersValue : data.avg_mrr_per_customer;

  // Last-N-month averages from Xero P&L (via Budget Tracker actuals)
  const gpPct = agg?.gm_pct_avg;
  // Rule of 40 — 12m rolling average across complete months
  const rule40 = agg?.rule_of_40_avg;
  // Run-rate EBITDA = last month net profit × 12
  const runRateEbitda = agg?.run_rate_ebitda_unadjusted;
  // Build "Mar, Apr, May" style range label from the oldest & newest months in the window
  let aggMonthsLabel = "Sync Xero P&L in Admin → Integrations";
  let lastMonthLabel = "—";
  if (agg?.months?.length) {
    const monthsAsc = agg.months.slice().reverse();
    const fmtM = (k) => new Date(k + "-02").toLocaleString("en-GB", { month: "short", year: "2-digit" });
    if (monthsAsc.length === 1) {
      aggMonthsLabel = `1m · ${fmtM(monthsAsc[0])}`;
    } else {
      aggMonthsLabel = `${monthsAsc.length}m · ${fmtM(monthsAsc[0])} – ${fmtM(monthsAsc[monthsAsc.length - 1])}`;
    }
    if (agg.last_month) lastMonthLabel = fmtM(agg.last_month);
  }
  const rule40Colour = rule40 == null ? "slate" : rule40 >= 40 ? "emerald" : rule40 >= 30 ? "amber" : "rose";

  return (
    <div className="space-y-6" data-testid="kpis-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Banknote} label={cmMrr != null ? "Run-rate MRR" : "Total MRR"} value={fmtMoney(mrrValue)} sub={mrrSub} accent="blue" testid="kpi-total-mrr" />
        <Stat icon={TrendingUp} label={cmArr != null ? "Run-rate ARR" : "Total ARR"} value={fmtMoney(arrValue)} sub={arrSub} accent="emerald" testid="kpi-total-arr" />
        <Stat icon={Users} label="Customers" value={customersValue} sub={customersSub} accent="violet" testid="kpi-customers" />
        <Stat icon={Banknote} label="Avg MRR / customer" value={fmtMoney(avgMrr)} accent="blue" testid="kpi-avg-mrr" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          icon={Percent}
          label="GP % (12m avg)"
          value={gpPct != null ? `${gpPct.toFixed(1)}%` : "—"}
          sub={gpPct != null ? `Xero · ${aggMonthsLabel}` : aggMonthsLabel}
          accent="emerald"
          testid="kpi-gp-pct-xero"
        />
        <Stat
          icon={TrendingUp}
          label="Rule of 40 (12m avg)"
          value={rule40 != null ? `${rule40.toFixed(1)}%` : "—"}
          sub={
            rule40 != null
              ? `Growth ${(agg.growth_pct_avg ?? 0).toFixed(1)}% + Margin ${(agg.net_margin_pct_avg ?? 0).toFixed(1)}%`
              : aggMonthsLabel
          }
          accent={rule40Colour}
          testid="kpi-rule-of-40"
        />
        <Stat
          icon={Banknote}
          label="Run-Rate EBITDA"
          value={runRateEbitda != null ? fmtMoney(runRateEbitda) : "—"}
          sub={runRateEbitda != null ? `Unadjusted · ${lastMonthLabel} × 12` : aggMonthsLabel}
          accent="violet"
          testid="kpi-run-rate-ebitda"
        />
        <div className="hidden lg:block"></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* By industry */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading flex items-center gap-2"><Building2 className="h-4 w-4 text-blue-600" />Customers per industry</CardTitle></CardHeader>
          <CardContent className="p-5">
            {data.by_industry.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No active customers yet.</div>
            ) : (
              <div className="space-y-2">
                {data.by_industry.map((i) => (
                  <div key={i.industry} className="flex items-center gap-3" data-testid={`kpi-industry-${i.industry}`}>
                    <div className="w-40 text-sm text-slate-700 truncate" title={i.industry}>{i.industry}</div>
                    <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-600" style={{ width: `${(i.count / indMax) * 100}%` }} />
                    </div>
                    <div className="w-10 text-right font-mono text-sm font-medium">{i.count}</div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top GP % accounts */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100"><CardTitle className="text-base font-heading flex items-center gap-2"><Percent className="h-4 w-4 text-emerald-600" />Top GP % accounts</CardTitle></CardHeader>
          <CardContent className="p-0">
            {data.top_gp_accounts.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No won deals yet.</div>
            ) : (
              <table className="w-full text-sm" data-testid="kpi-top-gp-table">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                    <th className="px-3 py-2">Account</th>
                    <th className="px-3 py-2 text-right">Revenue</th>
                    <th className="px-3 py-2 text-right">GP</th>
                    <th className="px-3 py-2 text-right">GP %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_gp_accounts.map((a) => (
                    <tr key={a.account_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => nav(`/app/accounts/${a.account_id}`)}>
                      <td className="px-3 py-2 font-medium">{a.account_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(a.revenue)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtMoney(a.gp)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700 font-semibold">{a.gp_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top 10 customers by ARR */}
      {(() => {
        // Prefer ChartMogul as source of truth. Fall back to internal ARR if CM not connected.
        const cmTop = cm?.top_accounts || [];
        const usingCm = cmTop.length > 0;
        // Sort CM rows by ARR desc (CM returned MRR-sorted; we want ARR for display headline)
        const topRows = usingCm
          ? cmTop.slice().sort((a, b) => (b.arr || 0) - (a.arr || 0)).slice(0, 10).map((r) => {
              const acc = accountsMap[r.account_id] || {};
              return {
                id: r.account_id,
                name: r.account_name || acc.name,
                industry: acc.industry,
                num_users: acc.num_users || 0,
                arr: r.arr,
                mrr: r.mrr,
                health: acc.health,
                owner_name: acc.owner_name,
                status: r.status,
                chartmogul_url: r.chartmogul_url,
              };
            })
          : (data.top_customers || []);

        return (
          <Card className="border-slate-200">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="text-base font-heading flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-600" />
                Top 10 customers (by ARR)
                <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                  usingCm ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-50 text-slate-500 border-slate-200"
                }`} data-testid="top-customers-source-badge">
                  {usingCm ? "Live · ChartMogul" : "Internal · CRM"}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {topRows.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500">
                  {usingCm
                    ? "No linked ChartMogul customers yet. Link accounts via Admin → ChartMogul match."
                    : "No active customers."}
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="kpi-top-customers-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                      <th className="px-4 py-2">#</th>
                      <th className="px-4 py-2">Customer</th>
                      <th className="px-4 py-2">Industry</th>
                      <th className="px-4 py-2 text-right">Users</th>
                      <th className="px-4 py-2 text-right">ARR</th>
                      <th className="px-4 py-2 text-right">MRR</th>
                      <th className="px-4 py-2">{usingCm ? "Status" : "Health"}</th>
                      <th className="px-4 py-2">Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topRows.map((c, i) => (
                      <tr key={c.id || i} className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer" onClick={() => c.id && nav(`/app/accounts/${c.id}`)}>
                        <td className="px-4 py-2 font-mono text-slate-400">{i + 1}</td>
                        <td className="px-4 py-2 font-medium">{c.name}</td>
                        <td className="px-4 py-2 text-xs text-slate-600">{c.industry || "—"}</td>
                        <td className="px-4 py-2 text-right font-mono text-sm">{c.num_users || 0}</td>
                        <td className="px-4 py-2 text-right font-mono font-medium">{fmtMoney(c.arr)}</td>
                        <td className="px-4 py-2 text-right font-mono text-slate-700">{fmtMoney(c.mrr)}</td>
                        <td className="px-4 py-2">
                          {usingCm ? (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              c.status === "active" ? "bg-emerald-50 text-emerald-700"
                              : c.status === "cancelled" ? "bg-rose-50 text-rose-700"
                              : c.status === "past_due" ? "bg-amber-50 text-amber-700"
                              : "bg-slate-50 text-slate-600"
                            }`}>{c.status || "—"}</span>
                          ) : (
                            <>
                              <span className={`inline-block h-2 w-2 rounded-full mr-1 ${c.health === "red" ? "bg-red-500" : c.health === "amber" ? "bg-amber-500" : "bg-emerald-500"}`} />
                              <span className="text-xs text-slate-600 capitalize">{c.health || "green"}</span>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">{c.owner_name || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}


/* ---------------- ChartMogul Portfolio Card ---------------- */
function ChartMogulPortfolioCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/chartmogul/portfolio");
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "ChartMogul portfolio unavailable");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (error) return null;
  if (!data || data.linked_account_count === 0) return null;

  const g = data.global || {};
  const max = Math.max(...data.top_accounts.map((r) => r.mrr), 1);

  return (
    <Card className="border-slate-200" data-testid="cm-portfolio">
      <CardHeader className="border-b border-slate-100">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Banknote className="h-4 w-4 text-emerald-600" /> ChartMogul portfolio
            </CardTitle>
            <div className="text-xs text-slate-500 mt-1">
              Live from ChartMogul · {g.customers ?? "—"} customers · {g.as_of ? `as of ${g.as_of}` : ""}
              <span className="ml-2 text-slate-400">| {data.linked_account_count} linked to CRM</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <div className="text-[10px] uppercase font-mono text-slate-500 tracking-wider">MRR</div>
            <div className="font-heading text-2xl font-bold text-emerald-700 mt-0.5">{fmtMoney(g.mrr ?? 0)}</div>
            <div className="text-[11px] text-slate-500 font-mono">Linked: {fmtMoney(data.linked_mrr ?? 0)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono text-slate-500 tracking-wider">Run-rate ARR</div>
            <div className="font-heading text-2xl font-bold text-blue-700 mt-0.5">{fmtMoney(g.arr ?? 0)}</div>
            <div className="text-[11px] text-slate-500 font-mono">MRR × 12 · today</div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono text-slate-500 tracking-wider">ARPA</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">{fmtMoney(g.arpa ?? 0)}</div>
            <div className="text-[11px] text-slate-500 font-mono">Per account / yr</div>
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono text-slate-500 tracking-wider">Customer churn</div>
            <div className="font-heading text-2xl font-bold text-slate-900 mt-0.5">{(g.customer_churn_rate ?? 0).toFixed(1)}%</div>
            <div className="text-[11px] text-slate-500 font-mono">LTV {fmtMoney(g.ltv ?? 0)}</div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm" data-testid="cm-portfolio-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
              <th className="px-4 py-2">Account (linked)</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">MRR</th>
              <th className="px-4 py-2 text-right">Run-rate ARR</th>
              <th className="px-4 py-2 w-1/4">Share</th>
            </tr>
          </thead>
          <tbody>
            {data.top_accounts.map((r) => {
              const pct = (r.mrr / max) * 100;
              return (
                <tr key={r.account_id} onClick={() => nav(`/app/accounts/${r.account_id}`)}
                  className="border-b border-slate-100 hover:bg-blue-50/40 cursor-pointer" data-testid={`cm-portfolio-row-${r.account_id}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">{r.account_name}</td>
                  <td className="px-4 py-2 text-xs">
                    <span className={`px-2 py-0.5 text-[10px] uppercase font-mono tracking-wider rounded border ${r.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{fmtMoney(r.mrr)}</td>
                  <td className="px-4 py-2 text-right font-mono text-slate-600">{fmtMoney(r.arr)}</td>
                  <td className="px-4 py-2">
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
